import * as CryptoJS from 'crypto-js';
import { HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { formatDate } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { HEADER_AUDIT_REASON } from './constant';
import { AuditReason, encodeAuditReasons } from '../utils/audit-reason.utils';
import { DatePipe } from '@angular/common';

// Statuses already surfaced globally by the auth interceptor (network-unreachable,
// unauthenticated, forbidden) — components should still log them but must not
// toast a second time on top of the interceptor's own message.
const INTERCEPTOR_HANDLED_STATUSES = [0, 401, 403, 404, 405, 409, 500, 503];

export function handleApiError(err: HttpErrorResponse, toastr: ToastrService, fallbackMessage: string): void {
    console.error(err.message);
    if (!INTERCEPTOR_HANDLED_STATUSES.includes(err.status)) {
        toastr.error(err.message || fallbackMessage);
    }
}

export function cloneArray(json:any){
    let data = JSON.stringify(json)
    return JSON.parse(data);
}

export function getTimeZoneOffset(): string {
    const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return clientTimezone;
}

export function getTimeZoneHeader(){
    return new HttpHeaders().set('Timezone',getTimeZoneOffset())
  }

/** Common reason (string) or common + per-field reasons, sent Base64-encoded so any language is safe in a header. */
export function getAuditReasonHeader(reason: AuditReason){
    return new HttpHeaders().set(HEADER_AUDIT_REASON, encodeAuditReasons(reason))
  }

export function getTokenData(key: string) {
    return sessionStorage.getItem(key);
}

export function getDateTime(file: string){
    let d = new Date()
    var year = d.getFullYear();
    var month = (new Date().getMonth() + 1);
    var day = d.getDate();
    var hours = d.getHours();
    var minutes = d.getMinutes();
    var seconds = d.getSeconds();
    var dateStyle = day + "/" + month + "/" + year + "_" + hours + ":" + minutes + ":" + seconds
    let fileName =  file + "_" + dateStyle
    return fileName
}

export function getGMTDate(time:string) {
    if(time){
        var local = new Date(time);
        var offset = local.getTimezoneOffset();
        var utc = new Date(local.getTime() + offset * 60000);
        let datePipe = new DatePipe('EN')
        let test:any = datePipe.transform(utc.toISOString(),"yyyy-MM-dd HH:mm:ss.SSS")
        return  test.toString() || null;
    }
    return '';
}

export function getUtcDate(dateInput: Date | string): string {
  if (!dateInput) {
    return '';
  }
  return new Date(dateInput).toISOString();
}

export const AUDIT_DATE_KEYS = ['startDate', 'endDate', 'passwordChangedTime'];

// Convert a UTC ISO value ("2026-09-05T18:29:59.000Z") to local time as "17/09/2026 11:20:05 AM"
export function formatAuditChangeDate(value: string | number | undefined | null): string | number | undefined | null {
  if (value === null || value === undefined || value === '') return value;
  const dateObj = new Date(value);
  if (isNaN(dateObj.getTime())) return value;
  return formatDate(dateObj, 'dd/MM/yyyy hh:mm:ss a', 'en');
}

// Audit field names whose generated label would read badly
const AUDIT_PARAMETER_LABELS: Record<string, string> = {
  logoUrl: 'Study Logo',
  subjectId: 'Subject Id'
};

export function humanizeParameter(camelCaseParameter: string): string {
  if (!camelCaseParameter) return camelCaseParameter;
  if (AUDIT_PARAMETER_LABELS[camelCaseParameter]) return AUDIT_PARAMETER_LABELS[camelCaseParameter];
  const words = camelCaseParameter.split(/(?<=[a-z])(?=[A-Z])/);
  const last = words[words.length - 1];
  if (last.toLowerCase() === 'id') {
    words.pop();
  } else if (last.toLowerCase() === 'ids') {
    words.pop();
    if (words.length > 0) {
      words[words.length - 1] = words[words.length - 1] + 's';
    }
  }
  if (words.length === 0) return camelCaseParameter;
  return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export function humanizeBooleanValue<T>(value: T): T | string {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string' && (value.toLowerCase() === 'true' || value.toLowerCase() === 'false')) {
    return value.toLowerCase() === 'true' ? 'Yes' : 'No';
  }
  return value;
}

export function formatAuditChangeDates<
  T extends { parameter: string; oldValue?: unknown; newValue?: unknown }
>(changes: T[]): T[] {
  return changes.map(change => {
    const parameter = humanizeParameter(change.parameter);
    if (AUDIT_DATE_KEYS.includes(change.parameter)) {
      return {
        ...change,
        parameter,
        oldValue: formatAuditChangeDate(change.oldValue as string | number | undefined | null),
        newValue: formatAuditChangeDate(change.newValue as string | number | undefined | null)
      };
    }
    return {
      ...change,
      parameter,
      oldValue: humanizeBooleanValue(change.oldValue),
      newValue: humanizeBooleanValue(change.newValue)
    };
  });
}

export function validateFilterRequestLength(
    filterValues: unknown[][],
    isLengthWarningShown: boolean,
    showToast = true,
    showWarning?: () => void
): { isValid: boolean; isLengthWarningShown: boolean } {
    const MAX_FILTER_PARAM_LENGTH = 2000;
    const getFilterParamLength = (values: unknown[][]): number => {
        return values.reduce((total, vals) => {
            if (!Array.isArray(vals) || vals.length === 0) {
                return total;
            }
            return total + encodeURIComponent(vals.join(',')).length;
        }, 0);
    };

    const isValid = getFilterParamLength(filterValues) <= MAX_FILTER_PARAM_LENGTH;

    if (!isValid && showToast && !isLengthWarningShown) {
        showWarning?.();
        return { isValid, isLengthWarningShown: true };
    }

    if (isValid) {
        return { isValid, isLengthWarningShown: false };
    }

    return { isValid, isLengthWarningShown };
}

export function encryptPassword(password: string): string {
  const SECRET_KEY = 'c3d38e001f3bb6cf7b5bf91bd5648d0163b600cf3cea1c8263ee836f8ae4423c';
  const key = CryptoJS.enc.Hex.parse(SECRET_KEY);
  return CryptoJS.AES.encrypt(password, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();
}
